import { Link } from 'react-router-dom';

export const PrivacyContent = () => (
  <>
    <p>
      This Privacy Notice explains how Longitude Labs, Inc.
      (&ldquo;Meridian&rdquo;) collects, uses, discloses, and otherwise
      processes personal data in connection with Meridian&rsquo;s brands, to
      include Spreadsheet Arena (spreadsheetarena.ai), and Meridian AI
      (meridian.ai), as well as any specific product, service, or application
      that references or links to this Privacy Notice.
    </p>

    <p>
      This Privacy Notice does not address our privacy practices relating to
      Meridian job applicants, employees and other employment-related
      individuals, nor data that is not subject to applicable data protection
      laws (such as deidentified or publicly available information in certain
      jurisdictions). This Privacy Notice is also not a contract and does not
      create any legal rights or obligations not otherwise provided by law.
    </p>

    <h2>Our Role in Processing Personal Data</h2>

    <p>
      Data protection laws sometimes differentiate between
      &ldquo;controllers&rdquo; and &ldquo;processors&rdquo; of personal data. A
      &ldquo;controller&rdquo; determines the purposes and means (the why and
      how) of processing personal data. A &ldquo;processor,&rdquo; which is
      sometimes referred to as a &ldquo;service provider,&rdquo; processes
      personal data on behalf of a controller subject to the controller&rsquo;s
      instructions.
    </p>

    <p>
      This Privacy Notice describes our privacy practices where we are acting as
      the controller of personal data. However, this Privacy Notice does not
      cover or address how our customers may process personal data when they use
      our services, or how we may process personal data on their behalf in
      accordance with their instructions where we are acting as their processor.
      As a result, we recommend referring to the privacy notice of the customer
      with which you have a relationship for information on how they engage
      processors, like us, to process personal data on their behalf. In
      addition, we are generally not permitted to respond to individual requests
      relating to personal data we process on behalf of our customers, so we
      recommend directing any requests to the relevant customer.
    </p>

    <p>
      If you access our services or otherwise interact with us from the European
      Economic Area, United Kingdom, or Switzerland, please see the Controller
      Details and Privacy Contacts section of our Additional European Economic
      Area, United Kingdom, and Switzerland Privacy Disclosures for more
      information about our legal entities operating as the controllers of your
      personal data.
    </p>

    <h2>Our Collection and Use of Personal Data</h2>

    <p>
      The categories of personal data we collect depend on how you interact with
      us and our services. For example, you may provide us your personal data
      directly when you sign up for our newsletter, register for an account,
      make a purchase, post a review, participate in an event, contest,
      promotion or survey or otherwise contact us or interact with us.
    </p>

    <p>
      We also collect personal data automatically when you interact with our
      websites and other services and may also collect personal data from other
      sources and third parties.
    </p>

    <h3>Personal Data Provided by Individuals</h3>

    <p>
      We collect the following categories of personal data individuals provide
      us:
    </p>

    <ul>
      <li>
        <strong>Contact Information</strong>, including first and last name,
        phone number, email address, mailing address, and communication
        preferences. We use this information primarily to fulfill your request
        or transaction and to communicate with you in accordance with your
        preferences.
      </li>
      <li>
        <strong>Professional Information</strong>, including job title, company
        name, professional background, and the nature of your relationship with
        us. We use this information primarily to fulfill your request or
        transaction, to determine how we communicate with you, to administer
        your account, to provide you with our services, and for customer support
        purposes.
      </li>
      <li>
        <strong>Account Information</strong>, including first and last name,
        email address, phone number, username, account credentials or one-time
        passcodes, and the products or services you are interested in,
        purchased, or have otherwise used. We use this information primarily to
        administer your account, provide you with our products and services,
        communicate with you regarding your account and your use of our products
        and services, and for customer support purposes.
      </li>
      <li>
        <strong>Spreadsheet Arena Data</strong>: If you choose to participate in
        Spreadsheet Arena, we will collect additional personal data in order to
        facilitate those services including user-submitted AI prompts, AI model
        outputs, voting / preference data, and information you choose to share
        through your profile.
      </li>
      <li>
        <strong>Payment Information</strong>, including payment card
        information, billing address, and other financial information (such as,
        routing and account number). Please note that we use third-party payment
        providers, including Stripe, to process payments made to us. We do not
        retain any personally identifiable financial information, such as
        payment card number, you provide these third-party payment providers in
        connection with payments. Rather, all such information is provided
        directly by you to our third-party payment providers. The payment
        provider&rsquo;s use of your personal data is governed by their privacy
        notice.
      </li>
      <li>
        <strong>Event, Contest, Promotion, and Survey Information</strong>,
        including information provided when you sign up for an event, enter a
        contest or promotion, complete a survey or submit a testimonial. We use
        this information primarily to administer and facilitate our products and
        services, respond to your submission, communicate with you, conduct
        market research, inform our marketing and advertising activities,
        improve and grow our business, and facilitate the related event,
        contest, promotion, or survey.
      </li>
      <li>
        <strong>Feedback and Support Information</strong>, including the
        contents of custom messages sent through the forms, chat platforms,
        including any available online live chat or automated chat functions,
        email addresses, or other contact information we make available to
        customers, as well as recordings of calls or meetings with us, where
        permitted by law (including through the use of automated or artificial
        intelligence tools provided by us or our third-party providers). We use
        this information primarily to investigate and respond to your inquiries,
        to communicate with you via online chat, email, phone, text message or
        social media, and to improve our products and services.
      </li>
    </ul>

    <h3>Personal Data Automatically Collected</h3>

    <p>
      We, and our third-party partners, automatically collect information you
      provide to us and information about how you access and use our products
      and services when you engage with us. We typically collect this
      information through the use of a variety of our own and our third-party
      partners&rsquo; automatic data collection technologies, including (i)
      cookies or small data files that are stored on an individual&rsquo;s
      computer and (ii) other, related technologies, such as web beacons,
      pixels, embedded scripts, mobile SDKs, location-identifying technologies
      and logging technologies. Information we collect automatically about you
      may be combined with other personal data we collect directly from you or
      receive from other sources.
    </p>

    <p>
      We, and our third-party partners, use automatic data collection
      technologies to automatically collect the following data when you use our
      services or otherwise engage with us:
    </p>

    <ul>
      <li>
        <strong>Information About Your Device and Network</strong>, including
        the device type, manufacturer, and model, operating system, IP address,
        browser type, Internet service provider, and unique identifiers
        associated with you, your device, or your network (including, for
        example, a persistent device identifier). We employ third-party
        technologies designed to allow us to recognize when two or more devices
        are likely being used by the same individual and may leverage these
        technologies (where permitted by law) to link information collected from
        different devices.
      </li>
      <li>
        <strong>
          Information About the Way Individuals Use Our Services and Interact
          With Us
        </strong>
        , including the site from which you came, the site to which you are
        going when you leave our services, how frequently you access our
        services, whether you open emails or click the links contained in
        emails, whether you access our services from multiple devices, and other
        browsing behavior and actions you take on our services (such as the
        pages you visit, the content you view, videos you watch, the
        communications you have through our services, and the content and links
        you interact with). We employ third-party technologies designed to allow
        us to collect detailed information about browsing behavior and actions
        that you take on our services, which may record your mouse movements,
        scrolling, clicks, and keystroke activity on our services and other
        browsing, search or purchasing behavior. These third-party technologies
        may also record information you enter when you interact with our
        products or services, or engage in chat features or other communication
        platforms we provide.
      </li>
      <li>
        <strong>Information About Your Location</strong>, including general
        geographic location that we or our third-party providers may derive from
        your IP address.
      </li>
    </ul>

    <p>
      All of the information collected automatically through these tools allows
      us to improve your experience. For example, we may use this information to
      enhance and personalize your user experience, to monitor and improve our
      products and services, to offer communications features such as live and
      automated chat, and to improve the effectiveness of our products,
      services, offers, advertising, communications and customer service. We may
      also use this information to: (a) remember information so that you will
      not have to re-enter it during your visit or the next time you visit the
      site; (b) provide custom, personalized content and information, including
      targeted content; (c) identify you across multiple devices; (d) provide
      and monitor the effectiveness of our services; (e) monitor aggregate
      metrics such as total number of visitors, traffic, usage, and demographic
      patterns on our website; (f) diagnose or fix technology problems; and (g)
      otherwise to plan for and enhance our products and services.
    </p>

    <p>
      For information about the choices you may have in relation to our use of
      automatic data collection technologies, please refer to the Your Privacy
      Choices section below. For additional details about the cookies we use on
      our websites and to adjust your preferences with regard to those cookies,
      please visit our &ldquo;Cookie Settings&rdquo; manager linked in the
      footer of our website.
    </p>

    <h3>Personal Data from Other Sources and Third Parties</h3>

    <p>
      We may receive the same categories of personal data as described above
      from the following sources and other parties:
    </p>

    <ul>
      <li>
        <strong>Single Sign-On</strong>: We may provide you the ability to log
        in to our services through certain third-party accounts you maintain.
        When you use these single sign-on protocols to access our services, we
        do not receive your login credentials for the relevant third-party
        service. Instead, we receive tokens from the single sign-on protocol to
        help identify you in our system (such as by your username) and confirm
        you successfully authenticated through the single sign-on protocol. This
        information allows us to more easily provide you access to our products
        and services.
      </li>
      <li>
        <strong>Mobile Sign-On</strong>: We may provide you the ability to log
        in to our mobile applications or authenticate yourself using facial,
        fingerprint, or other biometric recognition technology available through
        your mobile device. If you choose to utilize these login features,
        information about your facial geometry, your fingerprint, or other
        biometric information will be collected by your mobile device for
        authentication purposes. We do not store or have access to this
        biometric information. Instead, your mobile device will perform the
        biometric authentication process and only let us know whether the
        authentication was successful. If the authentication was successful, you
        will be able to access the applicable mobile application or feature
        without separately providing your credentials. For more details, please
        refer to the biometric authentication guides offered by your device
        provider.
      </li>
      <li>
        <strong>Employers</strong>: If you interact with our services in
        connection with your employment, we may obtain personal data about you
        from your employer or another company for which you work. For example,
        we may obtain your contact information from your employer to allow us to
        communicate with you about your employer&rsquo;s customer relationship
        with us.
      </li>
      <li>
        <strong>Other Customers and Users</strong>: We may receive your personal
        data from our other customers and users. For example, a customer may
        provide us with your contact information as a part of a referral or
        another user may rate your contributions to one or more of our services.
      </li>
      <li>
        <strong>Social Media</strong>: When you interact with our services
        through other social media networks, such as when you follow us or share
        our content on other social networks, we may receive some information
        that you permit the social network to share with third parties. The data
        we receive is dependent upon your privacy settings with the social
        network, and may include profile information, username, and any other
        information you permit the social network to share with third parties.
        You should always review and, if necessary, adjust your privacy settings
        on third-party websites and social media networks and services before
        sharing information and/or linking or connecting them to other services.
        We use this information primarily to operate, maintain, and provide to
        you the features and functionality of our products and services, as well
        as to communicate directly with you, such as to send you messages about
        features that may be of interest to you.
      </li>
      <li>
        <strong>Advertisers, Influencers, and Publishers</strong>: We engage in
        advertising through third-party services. Advertisers, influencers, and
        publishers may share personal data with us in connection with our
        advertising efforts. For example, we may obtain information about
        whether an advertisement for our services led to a successful engagement
        between you and us.
      </li>
      <li>
        <strong>Service Providers</strong>: Our service providers that perform
        services on our behalf, such as analytics and certain marketing
        providers, collect personal data and often share some or all of this
        information with us. For example, we receive personal data you may
        submit in response to requests for feedback to our survey providers.
      </li>
      <li>
        <strong>Other Sources</strong>: We may also collect personal data about
        you from other sources, including publicly available sources, or through
        transactions such as mergers and acquisitions.
      </li>
    </ul>

    <h3>Additional Uses of Personal Data</h3>

    <p>
      In addition to the primary purposes for using personal data described
      above, we may also use personal data we collect to:
    </p>

    <ul>
      <li>
        Fulfill or meet the reason the information was provided, such as to
        fulfill our contractual obligations, to facilitate payment for our
        products and services, or to deliver the services requested;
      </li>
      <li>Manage our organization and its day-to-day operations;</li>
      <li>
        Communicate with you, including via email, text message, chat, social
        media and/or telephone calls;
      </li>
      <li>
        Facilitate the relationship we have with you and, where applicable, the
        company you represent;
      </li>
      <li>
        Request you provide us feedback about our product and service offerings;
      </li>
      <li>
        Address inquiries or complaints made by or about an individual in
        connection with our products or services;
      </li>
      <li>Create and maintain accounts for our users;</li>
      <li>
        Verify your identity and entitlement to our products and services;
      </li>
      <li>
        Register you for and provide you access to events, contests,
        sweepstakes, and surveys;
      </li>
      <li>
        Market our products and services to you, including through email, phone,
        text message, push notification, and social media;
      </li>
      <li>
        Administer, improve, and personalize our products and services,
        including by recognizing you and remembering your information when you
        return to our products and services;
      </li>
      <li>
        Develop, operate, improve, maintain, protect, and provide the features
        and functionality of our products and services;
      </li>
      <li>Identify and analyze how you use our products and services;</li>
      <li>
        Infer additional information about you from your use of our products and
        services, such as your interests;
      </li>
      <li>
        Create aggregated or de-identified information that cannot reasonably be
        used to identify you, which information we may use for purposes outside
        the scope of this Privacy Notice;
      </li>
      <li>
        Conduct research and analytics on our user base and our products and
        services, including to better understand the demographics of our users;
      </li>
      <li>
        Improve and customize our products and services to address the needs and
        interests of our user base and other individuals we interact with;
      </li>
      <li>
        Test, enhance, update, and monitor the products and services, or
        diagnose or fix technology problems;
      </li>
      <li>
        Help maintain and enhance the safety, security, and integrity of our
        property, products, services, technology, assets, and business;
      </li>
      <li>
        Defend, protect, or enforce our rights or applicable contracts and
        agreements (including our <Link to="/terms">Terms of Service</Link>), as
        well as to resolve disputes, to carry out our obligations and enforce
        our rights, and to protect our business interests and the interests and
        rights of third parties;
      </li>
      <li>
        Detect, prevent, investigate, or provide notice of security incidents or
        other malicious, deceptive, fraudulent, or illegal activity and protect
        the rights and property of Meridian AI and others;
      </li>
      <li>
        Facilitate business transactions and reorganizations impacting the
        structure of our business;
      </li>
      <li>Comply with contractual and legal obligations and requirements;</li>
      <li>
        Fulfill any other purpose for which you provide your personal data, or
        for which you have otherwise consented.
      </li>
    </ul>

    <h2>Our Disclosure of Personal Data</h2>

    <p>
      We disclose or otherwise make available personal data in the following
      ways:
    </p>

    <ul>
      <li>
        <strong>To Your Employer</strong>: If you interact with our services in
        connection with your employment, we may disclose personal data to your
        employer or another company for which you work. For example, we may
        provide information to your employer about your usage of our services in
        connection with your work for them.
      </li>
      <li>
        <strong>To Other Customers, Users or the General Public</strong>: We
        make available personal data designed for sharing through our services
        with certain or all other Meridian customers and users, or the general
        public, based on the applicable privacy settings and intended
        recipients. We may also make your username and test prompts available to
        the public or other users when you choose to engage in testing / voting
        in Spreadsheet Arena.
      </li>
      <li>
        <strong>To Marketing Providers</strong>: We coordinate and share
        personal data with our marketing providers in order to advertise and
        communicate with you about the products and services we make available.
      </li>
      <li>
        <strong>To Ad Networks and Advertising Partners</strong>: We work with
        third-party ad networks and advertising partners to deliver advertising
        and personalized content across websites, services, and devices. These
        parties may collect information automatically from your browser or
        device when you visit our websites and other services through the use of
        cookies and related technologies. This information is used to provide
        and inform targeted advertising, as well as to provide
        advertising-related services such as reporting, attribution, analytics,
        and market research.
      </li>
      <li>
        <strong>To Service Providers</strong>: We engage other third parties to
        perform certain services on our behalf in connection with the uses of
        personal data described in the sections above. Depending on the
        applicable services, these service providers may process personal data
        on our behalf or have access to personal data while performing services
        on our behalf.
      </li>
      <li>
        <strong>To Other Businesses as Needed to Provide Services</strong>: We
        may share personal data with third parties you engage with through our
        services or as needed to fulfill a request or transaction including, for
        example, payment processing services.
      </li>
      <li>
        <strong>
          In Connection with a Business Transaction or Reorganization
        </strong>
        : We may take part in or be involved with a business transaction or
        reorganization, such as a merger, acquisition, joint venture, or
        financing or sale of company assets. We may disclose, transfer, or
        assign personal data to a third party during negotiation of, in
        connection with, or as an asset in such a business transaction or
        reorganization. Also, in the unlikely event of our bankruptcy,
        receivership, or insolvency, your personal data may be disclosed,
        transferred, or assigned to third parties in connection with the
        proceedings or disposition of our assets.
      </li>
      <li>
        <strong>To Facilitate Legal Obligations and Rights</strong>: We may
        disclose personal data to third parties, such as legal advisors and law
        enforcement:
        <ul>
          <li>
            in connection with the establishment, exercise, or defense of legal
            claims;
          </li>
          <li>
            to comply with laws or to respond to lawful requests and legal
            process;
          </li>
          <li>
            to protect our rights and property and the rights and property of
            our agents, customers, and others, including to enforce our
            agreements, policies, and terms of use;
          </li>
          <li>to detect, suppress, or prevent fraud;</li>
          <li>to reduce credit risk and collect debts owed to us;</li>
          <li>
            to protect the health and safety of us, our customers, or any
            person; or
          </li>
          <li>as otherwise required by applicable law.</li>
        </ul>
      </li>
      <li>
        <strong>With Your Consent or Direction</strong>: We may disclose your
        personal data to certain other third parties or publicly with your
        consent or direction. For example, with your permission, we may post
        your testimonial on our websites.
      </li>
    </ul>

    <h2>Your Privacy Choices</h2>

    <h3>Communication Preferences</h3>

    <ul>
      <li>
        <strong>Email Communication Preferences</strong>: You can stop receiving
        promotional email communications from us by clicking on the
        &ldquo;unsubscribe&rdquo; link provided in any of our email
        communications. Please note you cannot opt-out of service-related email
        communications (such as, account verification, transaction confirmation,
        or service update emails).
      </li>
      <li>
        <strong>Phone Communication Preferences</strong>: You can stop receiving
        promotional phone communications from us by informing the caller you no
        longer wish to receive promotional phone calls from us, following the
        instructions provided on the call for opting out of promotional phone
        calls (where available), or replying STOP to any one of our promotional
        text messages. Please note we may need to continue to communicate with
        you via phone for certain service-related messages (such as, sending a
        verification code to your phone via call or text for purposes of
        verifying the authenticity of a log-in attempt).
      </li>
      <li>
        <strong>Direct Mailing Preferences</strong>: You can stop receiving
        promotional direct mail communications from us by contacting us as set
        forth in the Contact Us section below. Please note this opt-out does not
        affect any mailings that are controlled by third parties that may
        feature or mention our services.
      </li>
    </ul>

    <h3>Automatic Data Collection Preferences</h3>

    <p>
      You may use the &ldquo;Cookie Settings&rdquo; manager linked in the footer
      of our website to adjust your preferences regarding certain automatic data
      collection technologies on the specific website you are visiting for the
      specific device and browser you are using at that time (which means you
      will need to change your preferences on each device and browser you use to
      interact with the specific website you are visiting).
    </p>

    <p>
      You may also be able to utilize third-party tools and features to restrict
      our use of automatic data collection technologies. For example, (i) most
      browsers allow you to change browser settings to limit automatic data
      collection technologies on websites, (ii) most email providers allow you
      to prevent the automatic downloading of images in emails that may contain
      automatic data collection technologies, and (iii) many devices allow you
      to change your device settings to limit automatic data collection
      technologies for device applications. Please note that blocking automatic
      data collection technologies through third-party tools and features may
      negatively impact your experience using our services, as some features and
      offerings may not work properly or at all. Depending on the third-party
      tool or feature you use, you may not be able to block all automatic data
      collection technologies or you may need to update your preferences on
      multiple devices or browsers. We do not have any control over these
      third-party tools and features and are not responsible if they do not
      function as intended.
    </p>

    <h3>Targeted Advertising Preferences</h3>

    <p>
      We engage third parties to help us facilitate targeted advertising
      designed to show you personalized ads based on predictions of your
      preferences and interests developed using personal data we maintain and
      personal data our third-party partners obtain from your activity over time
      and across nonaffiliated websites and other services. The data we and our
      third-party partners use for purposes of facilitating targeted
      advertising, as well as to provide advertising-related services such as
      reporting, attribution, analytics, and market research, are primarily
      collected through the use of a variety of automatic data collection
      technologies, including cookies, web beacons, pixels, embedded scripts,
      mobile SDKs, location-identifying technologies and logging technologies.
      We may share a common account identifier (such as a hashed email address
      or user ID) with our third-party advertising partners to help link the
      personal data we and our third-party partners collect to the same person,
      or otherwise target advertising to an individual on a third-party website
      or platform.
    </p>

    <p>
      In addition to taking the steps set forth in the Automatic Data Collection
      Preferences section above, you may be able to further exercise control
      over the advertisements that you see by leveraging one or more targeted
      advertising opt-out programs. For example:
    </p>

    <ul>
      <li>
        <strong>Device-Specific Opt-Out Programs</strong>: Certain devices
        provide individuals the option to turn off targeted advertising for the
        entire device (such as Apple devices through their App Tracking
        Transparency framework or Android devices through their opt out of ads
        personalization feature). Please refer to your device
        manufacturer&rsquo;s user guides for additional information about
        implementing any available device-specific targeted advertising
        opt-outs.
      </li>
      <li>
        <strong>Digital Advertising Alliance</strong>: The Digital Advertising
        Alliance allows individuals to opt out of receiving online
        interest-based targeted advertisements from companies that participate
        in their program.
      </li>
      <li>
        <strong>European Interactive Digital Advertising Alliance</strong>: The
        European Interactive Digital Advertising Alliance similarly allows
        individuals to opt out of receiving online interest-based targeted
        advertisements from companies that participate in their program.
      </li>
      <li>
        <strong>Network Advertising Initiative</strong>: The Network Advertising
        Initiative also provides individuals instructions for further
        controlling how information is used for online advertising.
      </li>
      <li>
        <strong>Platform-Specific Opt-Out Programs</strong>: Certain third-party
        platforms provide individuals the option to turn off targeted
        advertising for the entire platform (such as certain social media
        platforms). Please refer to your platform provider&rsquo;s user guides
        for additional information about implementing any available
        platform-specific targeted advertising opt-outs.
      </li>
    </ul>

    <p>
      Please note that when you opt out of receiving interest-based
      advertisements through one of these programs, this does not mean you will
      no longer see advertisements from us or on our services. Instead, it means
      that the online ads you do see from relevant program participants should
      not be based on your interests. We are not responsible for the
      effectiveness of, or compliance with, any third parties&rsquo; opt-out
      options or programs or the accuracy of their statements regarding their
      programs. In addition, program participants may still use automatic data
      collection technologies to collect information about your use of our
      services, including for analytics and fraud prevention as well as any
      other purpose permitted under the applicable advertising industry program.
    </p>

    <h2>Children&rsquo;s Personal Data</h2>

    <p>
      Our services are not directed to, and we do not intend to, or knowingly,
      collect or solicit personal data from children under the age of 13. If an
      individual is under the age of 13, they should not use our services or
      otherwise provide us with any personal data either directly or by other
      means. If a child under the age of 13 has provided personal data to us, we
      encourage the child&rsquo;s parent or guardian to contact us to request
      that we remove the personal data from our systems. If we learn that any
      personal data we collect has been provided by a child under the age of 13,
      we will promptly delete that personal data.
    </p>

    <h2>Retention of Personal Data</h2>

    <p>
      We will usually retain the personal data we collect about you for no
      longer than reasonably necessary to fulfil the purposes for which it was
      collected, and in accordance with our legitimate business interests and
      applicable law. However, if necessary, we may retain personal data for
      longer periods of time as required under applicable law or as needed to
      resolve disputes or protect our legal rights.
    </p>

    <p>
      To determine the appropriate duration of the retention of personal data,
      we consider the amount, nature, and sensitivity of the personal data, the
      potential risk of harm from unauthorized use or disclosure of personal
      data and if we can attain our objectives by other means, as well as our
      legal, regulatory, tax, accounting, and other applicable obligations.
    </p>

    <p>
      Once retention of the personal data is no longer reasonably necessary for
      the purposes outlined above, we will either delete or deidentify the
      personal data or, if that is not possible (for example, because personal
      data has been stored in backup archives), we will securely store the
      personal data and isolate it from further active processing until deletion
      or deidentification is possible.
    </p>

    <h2>Third-Party Websites and Services</h2>

    <p>
      Our websites and other services may include links to or redirect you to
      third-party websites, plug-ins, applications, or other services, including
      social media services where you may connect with us. Third-party websites
      and other services may also reference or link to our websites and
      services. This Privacy Notice does not apply to any personal data
      practices of these third-party websites, plug-ins, applications, or other
      services. To learn about these third parties&rsquo; personal data
      practices, please visit their respective privacy notices.
    </p>

    <h2>Region-Specific Disclosures</h2>

    <p>
      We may choose or be required by law to provide different or additional
      disclosures relating to the processing of personal data about residents of
      certain countries, regions or states. Please refer below for disclosures
      that may be applicable to you:
    </p>

    <ul>
      <li>
        <strong>European Economic Area, United Kingdom or Switzerland</strong>:
        If you are located in the European Economic Area (Member States of the
        European Union together with Iceland, Norway, and Liechtenstein), the
        United Kingdom, or Switzerland, please see the Additional European
        Economic Area, United Kingdom, and Switzerland Privacy Disclosures
        section below for additional European-specific privacy disclosures,
        including a description of the personal data rights made available to
        individuals located in those jurisdictions under applicable law.
      </li>
      <li>
        <strong>United States</strong>: If you are a resident of the United
        States, please see the Additional United States Privacy Disclosures
        section below for additional U.S.-specific privacy disclosures,
        including a description of the personal data rights made available to
        residents of certain states under applicable law.
      </li>
    </ul>

    <h2>Updates to This Privacy Notice</h2>

    <p>
      We may update this Privacy Notice from time to time. When we make changes
      to this Privacy Notice, we will change the date at the beginning of this
      Privacy Notice. If we make material changes to this Privacy Notice, we
      will notify individuals by email to their registered email address, by
      prominent posting on this website or our other platforms, or through other
      appropriate communication channels. All changes shall be effective from
      the date of publication unless otherwise provided.
    </p>

    <h2>Contact Us</h2>

    <p>
      If you have any questions or requests in connection with this Privacy
      Notice or other privacy-related matters, please contact us at:{' '}
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>.
    </p>

    <h2>
      Additional European Economic Area, United Kingdom, and Switzerland Privacy
      Disclosures
    </h2>

    <p>
      These disclosures supplement the information contained in our Privacy
      Notice by providing additional information about our personal data
      processing practices relating to individuals who access our services or
      otherwise interact with us from the European Economic Area
      (&ldquo;EEA&rdquo;), United Kingdom (&ldquo;UK&rdquo;), and Switzerland.
      For a detailed description of how we collect, use, disclose, and otherwise
      process personal data, please read our Privacy Notice.
    </p>

    <h3>Controller Details and Privacy Contacts</h3>

    <p>
      <strong>EEA, UK, and Swiss Controller</strong>
    </p>

    <p>
      Longitude Labs, Inc., a company duly incorporated and organised under the
      laws of the United States, having its registered address at 2 Gansevoort
      8F, New York, NY 10014, is the &ldquo;controller&rdquo; responsible for
      the processing of personal data in connection with our EEA, UK, and Swiss
      services and operations. This means Longitude Labs, Inc. determines and is
      responsible for how your personal data is used. You may contact Longitude
      Labs, Inc. by sending an email to{' '}
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>.
    </p>

    <h3>Additional Questions or Complaints</h3>

    <p>
      If you have a concern about our processing of personal data, you have the
      right to lodge a complaint with the Data Protection Authority where you
      reside, where you work, or where an alleged violation of the law has
      occurred.
    </p>

    <p>
      We would, however, appreciate the chance to handle your concerns directly
      prior to a complaint being filed, so please contact us directly at{' '}
      <a href="mailto:legal@meridian.ai">legal@meridian.ai</a> if you have any
      concerns.
    </p>

    <h3>Purposes and Legal Bases of Processing</h3>

    <p>
      When we process your personal data, we will do so in reliance on the
      following lawful bases:
    </p>

    <ul>
      <li>
        <strong>Contract</strong>: Where the processing is necessary for the
        performance of a contract between you and us, or to take steps at your
        request prior to entering into a contract with you. This applies to any
        processing where you sign a contract with us in your capacity as an
        individual, for example when you become our customer or deliver services
        to us as a vendor or contractor. This may also include processing
        necessary for the performance of our{' '}
        <Link to="/terms">Terms of Service</Link>.
      </li>
      <li>
        <strong>Legitimate Interest</strong>: Where the processing is necessary
        for the purposes of a legitimate interest that are not overridden by
        your interests or fundamental rights and freedoms (e.g., to provide,
        maintain, and improve our products and services, conduct data analytics,
        or to pursue or defend legal claims).
      </li>
      <li>
        <strong>Legal Obligation</strong>: Where the processing is necessary to
        comply with our legal obligations (e.g., to maintain a record of your
        personal data to comply with laws and regulations related to
        bookkeeping, accounting, taxation, and employment).
      </li>
      <li>
        <strong>Consent</strong>: Where we have your consent for the processing
        (e.g., when you opt in to receive marketing communications from us or to
        our collection of data through non-essential cookies). When consent is
        the legal basis for our processing of your personal data, you may
        withdraw your consent at any time.
      </li>
    </ul>

    <h3>Your Additional EEA, UK, and Swiss Privacy Choices</h3>

    <p>
      Subject to certain limitations at law, you may be able to exercise the
      following rights:
    </p>

    <ul>
      <li>
        <strong>Right to Access</strong>: The right to obtain confirmation of
        whether we are processing personal data about you, access to and a copy
        of the personal data we are processing about you, and information
        relating to its processing.
      </li>
      <li>
        <strong>Right of Portability</strong>: The right to obtain a copy of the
        personal data we have collected about you in a structured, commonly
        used, and machine-readable format, and the right to transmit that
        personal data to another controller without hindrance.
      </li>
      <li>
        <strong>Right to Rectification</strong>: The right to correct or update
        any personal data about you that is inaccurate or incomplete.
      </li>
      <li>
        <strong>Right to Restriction of Processing</strong>: The right to
        require us to limit the purposes for which we process your personal data
        if the continued processing of the personal data in this way is not
        justified, such as where the accuracy of the personal data is contested
        by you.
      </li>
      <li>
        <strong>Right to Object to Processing</strong>: The right to object to
        any processing based on our legitimate interests where there are grounds
        relating to your particular situation. There may be compelling reasons
        for continuing to process your personal data, and we will assess and
        inform you if that is the case. You can object to marketing activities
        for any reason.
      </li>
      <li>
        <strong>Right to Withdraw Consent</strong>: The right to withdraw your
        previously provided consent to our processing of your personal data.
        Please note withdrawing your consent will not affect the lawfulness of
        our use of your personal data before your consent was withdrawn, nor our
        processing of personal data pursuant to a different lawful basis for
        processing.
      </li>
      <li>
        <strong>Right to Erasure</strong>: The right to have us erase your
        personal data if the continued processing of that personal data is not
        otherwise justified.
      </li>
    </ul>

    <p>
      Please note that if the exercise of these rights limits our ability to
      process personal data, we may not be able to provide our services to you,
      or otherwise engage with you in the same manner.
    </p>

    <p>
      Please submit a request specifying the right you wish to exercise by
      emailing us at <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>.
    </p>

    <h2>Additional United States Privacy Disclosures</h2>

    <p>
      These disclosures supplement the information contained in our Privacy
      Notice by providing additional information about our personal data
      processing practices relating to individual residents of certain states in
      the United States. For a detailed description of how we collect, use,
      disclose, and otherwise process personal data, please read our Privacy
      Notice.
    </p>

    <h3>Nevada Residents</h3>

    <p>
      If you are a resident of the state of Nevada in the United States, you
      have the right to opt out of the sale of your personal data. You may
      submit a request to opt-out of the sale of your personal data by emailing
      us at <a href="mailto:legal@meridian.ai">legal@meridian.ai</a>.
    </p>

    <h3>Other United States Residents</h3>

    <p>
      If you are a resident of the state of California, Colorado, Connecticut,
      Delaware, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska,
      New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah,
      or Virginia in the United States the following supplementary disclosures
      apply to you.
    </p>

    <h3>Your Additional U.S. Privacy Choices</h3>

    <p>
      Depending on your state of residency and subject to certain legal
      limitations and exceptions, you may be able to exercise some or all of the
      following rights:
    </p>

    <ul>
      <li>
        <strong>Right to Know</strong>: The right to confirm whether we are
        processing personal data about you and, for residents of certain states,
        to obtain certain personalized details about the personal data we have
        collected about you.
      </li>
      <li>
        <strong>Right to Access &amp; Portability</strong>: The right to obtain
        access to the personal data we have collected about you and, where
        required by law, the right to obtain a copy of the personal data in a
        portable and, to the extent technically feasible, readily usable format
        that allows you to transmit the data to another entity without
        hindrance.
      </li>
      <li>
        <strong>Right to Correction</strong>: The right to correct inaccuracies
        in your personal data, taking into account the nature of the personal
        data and the purposes of the processing of the personal data.
      </li>
      <li>
        <strong>Right to Opt-Out of Targeted Advertising</strong>: The right to
        direct us not to use or share personal data for certain targeted
        advertising purposes.
      </li>
      <li>
        <strong>Right to Opt-Out of Sales</strong>: The right to direct us not
        to sell personal data to third parties, including the right to opt-out
        of the disclosure of personal data to third parties for the third
        parties&rsquo; direct marketing purposes under California&rsquo;s
        &ldquo;Shine the Light&rdquo; Law.
      </li>
      <li>
        <strong>Right to Deletion</strong>: The right to have us delete personal
        data we maintain about you.
      </li>
    </ul>

    <p>
      Please submit a request specifying the right you wish to exercise by
      emailing us at <a href="mailto:legal@meridian.ai">legal@meridian.ai</a> or
      calling our toll-free U.S. telephone number: 201-649-3742.
    </p>
  </>
);
